import { Box, Chip } from "@mui/material";

/**
 * Renders a flat key/value bag -- an Azure resource tag, typically.
 *
 * A single pair renders as its value alone, because the column header already names the
 * category. More than one renders as a chip per pair, key in muted text and value in
 * normal text.
 *
 * Before this, a tag field rendered as pretty-printed JSON, so a single tag
 * occupied three lines of monospace to say `{ "team": "opsblox azure" }`. Ten
 * of those on one detail page is unreadable.
 *
 * The input is whatever the API happened to send, so nothing here may throw:
 *   - an object                  -> chips
 *   - the JSON text of an object -> parsed, then chips
 *   - a plain string             -> rendered as itself
 *   - malformed JSON             -> rendered as the raw string
 *   - an array, or an object with
 *     a nested value             -> pretty-printed JSON, as before
 *   - null / undefined / {}      -> nothing
 *
 * The nested case deliberately keeps the old JSON rendering: flattening a
 * nested structure into a chip would either lose information or print
 * "[object Object]", and a readable blob beats a misleading chip.
 */

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** Only primitives fit in a chip; anything else keeps the JSON rendering. */
const isChippable = (value) =>
  value === null ||
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

/**
 * Coerce the incoming value to a tag object, or say it is not one.
 *
 * @returns {{tags: object}|{raw: *}|null} `tags` when there are pairs to chip,
 *   `raw` when the value should be rendered as-is, null when there is nothing.
 */
const readTags = (input) => {
  if (input === null || input === undefined || input === "") {
    return null;
  }

  if (isPlainObject(input)) {
    return Object.keys(input).length === 0 ? null : { tags: input };
  }

  if (typeof input !== "string") {
    // Arrays and anything exotic keep their existing rendering.
    return { raw: input };
  }

  const trimmed = input.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
    return { raw: input };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!isPlainObject(parsed)) {
      return { raw: input };
    }
    return Object.keys(parsed).length === 0 ? null : { tags: parsed };
  } catch {
    // Malformed JSON: show the user what is actually stored.
    return { raw: input };
  }
};

/** The pretty-printed fallback, matching what the detail page used to show. */
const jsonStyle = {
  fontFamily: "monospace",
  fontSize: "11px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const NiceTagValue = ({ children }) => {
  const read = readTags(children);

  if (read === null) {
    return null;
  }

  if (read.raw !== undefined) {
    return typeof read.raw === "string" ? (
      <span style={jsonStyle}>{read.raw}</span>
    ) : (
      <span style={jsonStyle}>{JSON.stringify(read.raw, null, 2)}</span>
    );
  }

  const entries = Object.entries(read.tags);

  if (!entries.every(([, value]) => isChippable(value))) {
    return <span style={jsonStyle}>{JSON.stringify(read.tags, null, 2)}</span>;
  }

  // A single pair renders as the value alone. cloudOrgStructure stores one category per
  // slot -- tag_1 is {"team": "opsblox azure"} -- and the column is now headed "Team(Tag)"
  // from that same category, so chipping the key repeated it in every row. This matches
  // how the Azure Consumption grid shows the same tags, which is the point: the two lists
  // are the same data and should not read differently.
  //
  // Multiple pairs keep their keys, because no single column header can name them all.
  if (entries.length === 1) {
    const [, value] = entries[0];

    return <span>{value === null ? "" : String(value)}</span>;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "0.3rem",
        alignItems: "center",
      }}
    >
      {entries.map(([key, value]) => (
        <Chip
          key={key}
          data-testid="tag-chip"
          size="small"
          variant="outlined"
          label={
            <Box
              component="span"
              sx={{ display: "inline-flex", gap: "0.3rem", alignItems: "baseline" }}
            >
              <Box component="span" sx={{ color: "text.secondary" }}>
                {key}
              </Box>
              <Box component="span">{value === null ? "" : String(value)}</Box>
            </Box>
          }
        />
      ))}
    </Box>
  );
};

export default NiceTagValue;
